/**
 * @packageDocumentation
 * @module f1/is
 */

import is_object from './is_object';
import is_array from './is_array';
import is_string from './is_string';

export interface IsEmpty {
  /**
   * Is a value empty?
   *
   * The conditions for emptiness are:
   * - an array is `length === 0`
   * - an object has not keys
   * - a string has no characters
   * - any other value is `null` or `undefined`
   *
   * @public
   * @function f1.is.empty
   * @example
   *
   * ```ts
   * f1.is.empty({}) => true
   * f1.is.empty({ a: 'a' }) => false
   *
   * f1.is.empty([]) => true
   * f1.is.empty(['a']) => false
   *
   * f1.is.empty('') => true
   * f1.is.empty('hello') => false
   * ```
   */
  (value: unknown): boolean;
}

/**
 * @see IsEmpty
 */
export default (value: unknown): boolean => {
  if (is_string(value)) {
    return value === '';
  } else if (is_object(value)) {
    const length = Object.getOwnPropertyNames(value).length;

    const isEmptyArray = length === 1 && is_array(value);
    const isLengthZero = length === 0;

    return isLengthZero || isEmptyArray;
  }

  return value == null;
};
